import { useState } from 'react';
import { Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, useAsync } from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import { knowledgeFor, type SkillToggle, type ToggleScope } from '../../api/knowledge';
import './knowledge.css';

type State = 'inherit' | 'on' | 'off';
const stateOf = (t: SkillToggle): State => (t.state === null ? 'inherit' : t.state ? 'on' : 'off');
const STATES: { value: State; label: string }[] = [
  { value: 'inherit', label: 'Inherit' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

/**
 * Which top-level skills a tenant, organization or system has: inherit / on / off per skill, saved at once (a switch
 * is a row of its own, independent of the surrounding editor). Only switches: a skill's text is edited on the Skills
 * page, a system skill's in buddy3's repository. Read-only without `ophub:buddy:admin`.
 */
export function SkillToggles({
  target,
  scope,
  scopeId,
}: {
  target: string;
  scope: ToggleScope;
  scopeId: string;
}) {
  const canWrite = useBuddyWrite();
  const rows = useAsync(
    () => (scopeId ? knowledgeFor(target).skills.toggles(scope, scopeId) : Promise.resolve([])),
    [target, scope, scopeId]
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async (key: string, next: State) => {
    const state = next === 'inherit' ? null : next === 'on';
    setBusy(key);
    setError(null);
    try {
      await knowledgeFor(target).skills.setToggles(scope, scopeId, { [key]: state });
      rows.setData((prev) => (prev ?? []).map((r) => (r.key === key ? { ...r, state } : r)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!scopeId)
    return <EmptyState title="Save this first" hint="Skills are switched once the record exists." />;
  if (rows.loading && !rows.data) return <Loading loading />;
  const list = rows.data ?? [];
  return (
    <div className="bdk-toggles" data-testid="skill-toggles">
      <p className="nyk-card-intro">
        Written procedures the agent may load. <b>Inherit</b> follows the level above; On or Off overrides it
        for this {scope}. The text of a skill is edited on the Skills page.
      </p>
      <ErrorBox error={rows.error ?? error} />
      {!rows.error && list.length === 0 && (
        <EmptyState
          title="No skills to switch"
          hint="Product skills appear once seeded; custom ones once written on the Skills page."
        />
      )}
      {list.map((t) => {
        const s = stateOf(t);
        const effective = t.state ?? t.inherited;
        return (
          <div key={t.key} className="bdk-toggle" data-skill={t.key} data-state={s}>
            <div className="bdk-toggle-text">
              <div className="bdk-toggle-name">
                <b>{t.name || t.key}</b>
                {t.origin === 'system' && (
                  <Chip size="sm" title="Ships with the product">
                    system
                  </Chip>
                )}
                <Chip size="sm" className={effective ? 'nyk-tone-green' : 'nyk-tone-grey'}>
                  {effective ? 'on' : 'off'}
                  {t.state === null ? ' (inherited)' : ''}
                </Chip>
              </div>
              {t.description && <div className="nyk-ui-hint">{t.description}</div>}
            </div>
            <div
              className="nyk-ui-segment bdk-segment"
              role="radiogroup"
              aria-label={`Skill ${t.key}`}
              aria-disabled={!canWrite || undefined}
            >
              {STATES.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={s === o.value}
                  className={`nyk-ui-segment-btn${s === o.value ? ' nyk-ui-selected' : ''}`}
                  disabled={!canWrite || busy === t.key}
                  title={o.value === 'inherit' ? `Inherited: ${t.inherited ? 'on' : 'off'}` : undefined}
                  onClick={() => s !== o.value && void save(t.key, o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
